// =============================================================================
// HERO SECTION COMPONENT
// =============================================================================

import React from 'react';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import type { ContentComponent } from '@/types';

interface HeroSectionProps {
  component: ContentComponent;
}

/**
 * Hero Section Component
 */
export const HeroSection: React.FC<HeroSectionProps> = ({ component }) => {
  return <HeroSectionImpl component={component} />;
};

/**
 * Hero Section Implementation
 */
const HeroSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const image = metadata?.image;
  const imageCredit = metadata?.imageCredit;
  const logo = metadata?.logo;
  const showLogo = metadata?.showLogo || false;
  const fullWidth = component.fullWidth || false;
  
  // Convert old ButtonConfig to new ActionConfig
  const actions = buttons.map(convertButtonToAction);
  
  // Check if image is a video file
  const isVideo = image && (image.endsWith('.mp4') || image.endsWith('.webm') || image.endsWith('.mov'));
  
  // Build display styles from old metadata (only for non-video images)
  const displayStyles: React.CSSProperties = {};
  if (image && fullWidth && !isVideo) {
    displayStyles.backgroundImage = `url(${image})`;
    displayStyles.backgroundSize = 'cover';
    displayStyles.backgroundPosition = 'center';
    displayStyles.backgroundRepeat = 'no-repeat';
  }
  
  if (fullWidth && image) {
    return (
      <section
        id={id}
        className="hero hero-fullwidth-bg"
        style={displayStyles}
      >
        {/* Video background for full-width hero */}
        {isVideo && (
          <video
            className="hero-video-bg"
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
          >
            <source src={image} type="video/mp4" />
          </video>
        )}
        
        <div className="hero-overlay">
          <div className="hero-content hero-content-centered">
            {showLogo && logo && (
              <div className="hero-logo">
                <img src={logo} alt="Awani" className="hero-logo-image" />
                <h1 className="hero-logo-text">Awani</h1>
              </div>
            )}
            <div className="hero-text">
              {!showLogo && <h1>{title}</h1>}
              {showLogo && <h2>{title}</h2>}
              <p>{content}</p>
              
              {actions.length > 0 && (
                <div className="hero-actions">
                  {actions.map((action, index) => (
                    <Button
                      key={index}
                      variant={action.variant || 'primary'}
                      onClick={createActionHandler(action)}
                    >
                      {action.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Image Credit - positioned relative to section */}
        {imageCredit && (
          <div className="hero-image-credit">
            <span dangerouslySetInnerHTML={{ __html: imageCredit }} />
          </div>
        )}
      </section>
    );
  }
  
  return (
    <section id={id} className="hero">
      <div className="container">
        <div className="hero-content">
          {image && (
            <div className="hero-image">
              {isVideo ? (
                <video
                  src={image}
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label={title}
                  className="hero-video"
                >
                  <source src={image} type="video/mp4" />
                </video>
              ) : (
                <img src={image} alt={title} loading="lazy" />
              )}
              {imageCredit && (
                <div className="hero-image-credit">
                  <span dangerouslySetInnerHTML={{ __html: imageCredit }} />
                </div>
              )}
            </div>
          )}
          
          <div className="hero-text">
            <h1>{title}</h1>
            <p>{content}</p>
            
            {actions.length > 0 && (
              <div className="hero-actions">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    onClick={createActionHandler(action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Export the implementation
export { HeroSectionImpl };

